import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface IncidentDetailModalProps {
  incidentId: string;
  onClose: () => void;
  onResolved?: () => void;
}

export default function IncidentDetailModal({
  incidentId,
  onClose,
  onResolved,
}: IncidentDetailModalProps) {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [actionText, setActionText] = useState('');
  const [correctiveTemp, setCorrectiveTemp] = useState('');

  const { data: incident, isLoading } = useQuery({
    queryKey: ['incident', incidentId],
    queryFn: () => apiClient.getIncidentById(incidentId),
  });

  const addActionMutation = useMutation({
    mutationFn: (text: string) =>
      apiClient.addCorrectiveAction(incidentId, text),
    onSuccess: () => {
      setActionText('');
      queryClient.invalidateQueries({ queryKey: ['incident', incidentId] });
      addToast(t('incidents.correctiveActionAdded', 'Action corrective ajoutée'), 'success');
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('incidents.correctiveActionError', 'Échec de l\'ajout de l\'action')), 'error');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: () => {
      const temp = parseFloat(correctiveTemp);
      return apiClient.closeIncident(incidentId, temp);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['temperature-logs'] });
      addToast(t('incidents.resolved', 'Incident résolu'), 'success');
      onResolved?.();
      onClose();
    },
    onError: (error: any) => {
      addToast(extractErrorMessage(error, t('incidents.resolveError', 'Échec de la résolution de l\'incident')), 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full my-4">
          <div className="text-center">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!incident) {
    return null;
  }

  const isResolved = incident.status === 'CLOSED';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full my-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {incident.type === 'TEMPERATURE' ? `🌡️ ${t('incidents.temperatureIncident', 'Incident de température')}` : t('incidents.title')}
              </h2>
              <p className="text-neutral-600 mt-2">
                {incident.equipment?.name || t('incidents.unknownEquipment', 'Équipement inconnu')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-neutral-600">{t('incidents.status')}</p>
              <div className="mt-2">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    isResolved
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {isResolved ? t('incidents.closed') : t('incidents.open')}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-600">{t('incidents.openedOn', 'Ouvert le')}</p>
              <p className="mt-2 text-neutral-900">
                {new Date(incident.openedAt).toLocaleString(i18n.language)}
              </p>
            </div>
          </div>

          {/* Temperature Details */}
          {incident.triggerTemperatureLog && (
            <div className="bg-neutral-50 rounded-lg p-4">
              <h3 className="font-medium text-neutral-900 mb-3">{t('incidents.temperatureDetails', 'Détails de température')}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-600">{t('temperature.temperature')}</p>
                  <p className="text-neutral-900 font-medium">
                    {incident.triggerTemperatureLog.temperatureC}°C
                  </p>
                </div>
                <div>
                  <p className="text-neutral-600">{t('incidents.status')}</p>
                  <p className="text-neutral-900 font-medium">
                    {incident.triggerTemperatureLog.conformity === 'OUT_OF_RANGE'
                      ? t('incidents.outOfRange', 'Hors plage')
                      : t('incidents.normal', 'Normal')}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-600">{t('incidents.measuredOn', 'Mesurée le')}</p>
                  <p className="text-neutral-900 font-medium">
                    {new Date(incident.triggerTemperatureLog.measuredAt).toLocaleString(i18n.language)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Corrective Actions */}
          <div>
            <h3 className="font-medium text-neutral-900 mb-3">{t('incidents.correctiveActions', 'Actions correctives')}</h3>
            <div className="space-y-3">
              {incident.correctiveActions && incident.correctiveActions.length > 0 ? (
                incident.correctiveActions.map((action: any) => (
                  <div key={action.id} className="bg-neutral-50 rounded-lg p-4">
                    <p className="text-neutral-900">{action.actionText}</p>
                    <p className="text-xs text-neutral-600 mt-2">
                      {new Date(action.createdAt).toLocaleString(i18n.language)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-neutral-600 text-sm">{t('incidents.noCorrectiveActions', 'Aucune action corrective pour le moment')}</p>
              )}
            </div>
          </div>

          {/* Add Corrective Action */}
          {!isResolved && (
            <div>
              <label className="block text-sm font-medium text-neutral-900 mb-2">
                {t('incidents.addCorrectiveAction', 'Ajouter une action corrective')}
              </label>
              <textarea
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                placeholder={t('incidents.correctiveActionPlaceholder', 'Décrivez l\'action corrective entreprise...')}
                className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
              />
              <button
                onClick={() => addActionMutation.mutate(actionText)}
                disabled={!actionText.trim() || addActionMutation.isPending}
                className="mt-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addActionMutation.isPending ? t('incidents.adding', 'Ajout...') : t('incidents.addAction', 'Ajouter l\'action')}
              </button>
            </div>
          )}

          {/* Corrective Temperature (shown when resolving) */}
          {!isResolved && incident.type === 'TEMPERATURE' && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <label className="block text-sm font-medium text-blue-900 mb-2">
                🌡️ {t('incidents.correctiveTempLabel', 'Température après correction')} *
              </label>
              <p className="text-xs text-blue-700 mb-3">
                {t('incidents.correctiveTempHint', 'Requis — cette température sera automatiquement ajoutée au relevé de températures')}
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  value={correctiveTemp}
                  onChange={(e) => setCorrectiveTemp(e.target.value)}
                  placeholder="Ex: 3.5"
                  className="w-32 px-3 py-2 rounded-lg border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <span className="text-sm font-medium text-blue-800">°C</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 p-6 bg-neutral-50 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-neutral-200 text-neutral-700 font-medium hover:bg-neutral-100 transition-colors"
          >
            {t('common.close')}
          </button>
          {!isResolved && (
            <button
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending || (incident.type === 'TEMPERATURE' && (!correctiveTemp.trim() || isNaN(parseFloat(correctiveTemp))))}
              className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resolveMutation.isPending ? t('incidents.resolving', 'Résolution...') : t('incidents.resolve', 'Résoudre l\'incident')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
